// catalog-history.jsx — the "Change history" card under the catalog grid: what changed,
// when, by whom, and through which action (src/change-log.js). Not the top bar's
// History, which is whole-file undo.
//
// Follows the same selection rule as the Notes card: a selected LINE shows that line
// and its family; otherwise the selected FAMILY shows itself and all its sizes.
//
// Same shape as CatalogWhereUsedTable beside it: always mounted, closed by default,
// fetched only when opened, `key` on the card so switching lines re-reads the open
// state and fires onOpen again for the new line. Internal logins only — the route is
// internal-only because rows name people and hold before-values from any owner.

import { useState, useEffect, useCallback } from 'react';
import Card from './components/Card.jsx';
import DataGrid from './components/DataGrid.jsx';
import Text from './components/Text.jsx';

const show = (v) => (v == null || v === '' ? '(empty)' : String(v));

const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || '';
  const p2 = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

// One sentence per row, in words: the change itself, not a code for it.
function describe(row) {
  if (row.op === 'update') return <><Text kind="code">{row.col}</Text> {show(row.old_value)} → {show(row.new_value)}</>;
  if (row.op === 'insert') return 'Created';
  if (row.op === 'delete') return 'Deleted';
  let s = {};
  try { s = JSON.parse(row.new_value || '{}'); } catch { /* keep the empty summary */ }
  const parts = [
    s.fields ? `${s.fields} field${s.fields === 1 ? '' : 's'} changed` : null,
    s.inserted ? `${s.inserted} added` : null,
    s.deleted ? `${s.deleted} deleted` : null,
  ].filter(Boolean);
  return `Bulk change on ${s.rows?.length ?? '?'} rows: ${parts.join(', ') || 'no detail'} (before-values are in that run's snapshot)`;
}

const HISTORY_COLS = [
  { key: 'at', label: 'When', width: 130, render: r => <Text kind="small">{when(r.at)}</Text> },
  { key: 'who', label: 'Who', width: 150, render: r => (r.who ? r.who : <Text kind="note">script</Text>) },
  { key: 'tbl', label: 'On', width: 60, render: r => (r.tbl === 'catalog_families' ? 'Family' : 'Line') },
  { key: 'change', label: 'Change', width: 320, render: describe },
  { key: 'label', label: 'Action', width: 160, render: r => (r.label ? r.label : <Text kind="empty" />) },
];

export default function CatalogHistoryCard({ lineId, familyId }) {
  const [hist, setHist] = useState(null); // { rows } | null
  const [loading, setLoading] = useState(false);

  // The thing shown: a line wins over a family, as on the Notes card.
  const target = lineId != null ? `lines/${lineId}` : familyId != null ? `families/${familyId}` : null;

  useEffect(() => { setHist(null); setLoading(false); }, [target]);

  const load = useCallback(() => {
    if (target == null || loading) return;
    setLoading(true);
    fetch(`/api/catalog/${target}/history`)
      .then(r => (r.ok ? r.json() : { rows: [] }))
      .then(h => setHist(h))
      .catch(err => { console.error(err); setHist({ rows: [] }); })
      .finally(() => setLoading(false));
  }, [target, loading]);

  const noSelection = target == null;
  const rows = hist?.rows ?? [];
  return (
    <Card
      key={target ?? 'none'}
      storageKey="catalog:section:history"
      title="Change history"
      count={noSelection ? 'nothing selected' : (hist != null ? `${rows.length}${rows.length >= 200 ? '+' : ''}` : undefined)}
      defaultOpen={false}
      onOpen={() => { if (!noSelection && hist == null) load(); }}
    >
      {noSelection ? <Text kind="note" as="div">Select a family or a line to see its change history.</Text>
        : hist == null ? <Text kind="note" as="div">Loading change history…</Text>
        : (
          <DataGrid
            rows={rows}
            columns={HISTORY_COLS}
            emptyMessage="No changes recorded yet. The log starts the day it was switched on."
            storageKey="catalog-line-history"
          />
        )}
    </Card>
  );
}
